import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './ShareBucketModal.module.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://wsticket.frozentoasty.de/api';

function ShareBucketModal({ isOpen, onClose, bucket, onShare }) {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && bucket && bucket._id) {
      fetchUsers();
      // Initialize selected users with currently shared users
      if (bucket.sharedWith) {
        setSelectedUsers(bucket.sharedWith.map(user => user._id));
      }
    }
  }, [isOpen, bucket]);

  const fetchUsers = async () => {
    if (!bucket || !bucket._id) {
      setError('Bucket ID is missing');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Fetch all users instead of just available ones
      const response = await axios.get(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Filter out the bucket owner from the list
      const filteredUsers = response.data.filter(user => user._id !== bucket.owner?._id && user._id !== bucket.owner);
      setUsers(filteredUsers);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
      console.error('Bucket object:', bucket);
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleShare = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.put(`${API_URL}/buckets/${bucket._id}/share`, {
        userIds: selectedUsers
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (onShare) {
        onShare(response.data);
      }
      
      onClose();
    } catch (err) {
      setError('Failed to update bucket sharing');
      console.error('Error updating bucket sharing:', err);
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Bucket "{bucket?.title}" teilen</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div className={styles.content}>
          <div className={styles.section}>
            <h3>Benutzer auswählen:</h3>
            {loading && <p>Lade Benutzer...</p>}
            {error && <div className={styles.error}>{error}</div>}
            
            {!loading && users.length > 0 && (
              <div className={styles.userList}>
                {users.map(user => {
                  const isShared = selectedUsers.includes(user._id);
                  return (
                    <div key={user._id} className={styles.userItem}>
                      <label className={styles.userLabel}>
                        <input
                          type="checkbox"
                          checked={isShared}
                          onChange={() => handleUserToggle(user._id)}
                        />
                        <span>{user.username}</span>
                        {bucket?.sharedWith?.some(sharedUser => sharedUser._id === user._id) && (
                          <span className={styles.currentlyShared}>(bereits geteilt)</span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!loading && users.length === 0 && (
              <p>Keine anderen Benutzer verfügbar.</p>
            )}
          </div>
        </div>
        
        <div className={styles.footer}>
          <button 
            className={styles.cancelButton} 
            onClick={onClose}
            disabled={loading}
          >
            Abbrechen
          </button>
          <button 
            className={styles.shareButton} 
            onClick={handleShare}
            disabled={loading}
          >
            {loading ? 'Speichern...' : 'OK'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ShareBucketModal;